import React, { useState, useEffect } from 'react';
import { Download, TrendingUp, DollarSign, FileCheck } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { AdminLayout } from './AdminLayout';
import { revenueAPI } from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

export const RevenueTracker = () => {
  const [selectedYear, setSelectedYear] = useState(2025);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: [],
    revenueByService: [],
    acceptedQuotesCount: 0
  });

  useEffect(() => {
    fetchStats();
  }, [selectedYear]);

  const fetchStats = async () => {
    try {
      const data = await revenueAPI.getStats(selectedYear);
      setStats(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  const handleExportCSV = async () => {
    try {
      await revenueAPI.exportCSV(selectedYear);
      toast.success('Fichier CSV exporté');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const formatChartData = () => {
    return stats.monthlyRevenue.map(item => ({
      month: new Date(item.month + '-01').toLocaleDateString('fr-FR', { month: 'short' }),
      revenue: item.revenue / 1000000 // Convert to millions
    }));
  };

  const statCards = [
    {
      title: 'CA Total',
      value: `${(stats.totalRevenue / 1000000).toFixed(2)}M FCFA`,
      icon: DollarSign,
      color: 'green',
      change: '+15%'
    },
    {
      title: 'Devis Acceptés',
      value: stats.acceptedQuotesCount,
      icon: FileCheck,
      color: 'blue',
      change: `${stats.acceptedQuotesCount} devis`
    },
    {
      title: 'Croissance',
      value: '+15%',
      icon: TrendingUp,
      color: 'orange',
      change: 'vs année dernière'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-green-100 text-green-600',
      blue: 'bg-blue-100 text-blue-600',
      orange: 'bg-orange-100 text-orange-600'
    };
    return colors[color] || colors.green;
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Chiffre d'Affaires</h1>
            <p className="text-gray-600">Analyse des revenus et performances</p>
          </div>
          <Button
            onClick={handleExportCSV}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Download className="h-5 w-5 mr-2" />
            Exporter CSV
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Card key={index} className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-600">{card.title}</p>
                    <div className={`p-2 rounded-lg ${getColorClasses(card.color)}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 mb-1">{card.value}</p>
                  <p className="text-sm text-gray-500">{card.change}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Revenue Chart */}
        <Card className="mb-8 bg-white">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Revenus mensuels ({selectedYear})</h2>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={formatChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: '#6b7280' }}
                />
                <YAxis 
                  tick={{ fill: '#6b7280' }}
                  label={{ value: 'Millions FCFA', angle: -90, position: 'insideLeft', fill: '#6b7280' }}
                />
                <Tooltip 
                  formatter={(value) => [`${value.toFixed(2)}M FCFA`, 'Revenus']}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <Legend />
                <Bar dataKey="revenue" fill="#16a34a" radius={[8, 8, 0, 0]} name="Revenus" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Revenue by Service */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Répartition par Service</h2>
              <div className="space-y-4">
                {stats.revenueByService.map((item, index) => {
                  const percentage = (item.revenue / stats.totalRevenue * 100).toFixed(1);
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">{item.service}</span>
                        <span className="text-sm text-gray-600">
                          {(item.revenue / 1000000).toFixed(2)}M FCFA ({percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Detailed Table */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Détails Mensuels</h2>
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {stats.monthlyRevenue.sort((a, b) => b.month.localeCompare(a.month)).map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-900">
                      {new Date(item.month + '-01').toLocaleDateString('fr-FR', { 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </span>
                    <span className="text-sm font-semibold text-green-600">
                      {(item.revenue / 1000000).toFixed(2)}M FCFA
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};